import React from 'react'
import { Layout, Typography, Row, Col, Card, Breadcrumb, Spin, Empty } from 'antd'
import { Link, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { siteSettingsApi, categoriesApi } from '../../services/api'
import { HomeOutlined } from '@ant-design/icons'

const { Content } = Layout
const { Title, Text, Paragraph } = Typography

const CategoryPage: React.FC = () => {
  const { i18n, t } = useTranslation()
  const location = useLocation()
  const currentLang = i18n.language as 'en' | 'zh'

  // Fetch site settings for dynamic content
  const { data: siteSettingsData } = useQuery({
    queryKey: ['publicSiteSettings'],
    queryFn: siteSettingsApi.getPublicSettings,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  // Fetch categories from database
  const { data: categoriesData, isLoading: categoriesLoading, error: categoriesError } = useQuery({
    queryKey: ['publicCategories'],
    queryFn: () => categoriesApi.getAll({ includeEmpty: false }),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  const settings = siteSettingsData?.settings

  // Helper function to get localized text
  const getLocalizedText = (text: { en: string; zh: string }, fallback: string = '') => {
    if (!text) return fallback
    return text[currentLang] || text.en || fallback
  }

  const isHomeProducts = location.pathname === '/home-products'
  
  // Get the main category based on the route
  const mainCategorySlug = isHomeProducts ? 'home-products' : 'medical-products'
  const mainCategory = categoriesData?.categories?.find((cat: any) => cat.slug === mainCategorySlug)
  
  const pageTitle = getLocalizedText(mainCategory?.name, isHomeProducts ? 'Home Products' : 'Medical Products')
  const pageDescription = getLocalizedText(mainCategory?.description, isHomeProducts 
    ? 'Comprehensive health monitoring solutions for home use'
    : 'Professional medical devices for clinical and healthcare settings')

  // Get subcategories for the current main category
  // The API returns subcategories nested within main categories
  const categories = mainCategory?.subcategories?.filter((cat: any) => cat.isActive)
    .sort((a: any, b: any) => a.sortOrder - b.sortOrder) || []

  return (
    <>
      <Helmet>
        <title>{pageTitle} - {getLocalizedText(settings?.siteName, 'MedCareHub')}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>

      <Content>
        {/* Breadcrumb */}
        <div className="bg-gray-50 py-4">
          <div className="max-w-6xl mx-auto px-4">
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to="/">
                  <HomeOutlined /> {t('navigation.home')}
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{pageTitle}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </div>

        {/* Banner Section: image or video (locale-aware) */}
        <section className="relative flex items-center justify-center h-[40vh] text-white">
          {(() => {
            const bannerByLang = isHomeProducts 
              ? settings?.pages?.categories?.homeProducts?.banner?.[currentLang]
              : settings?.pages?.categories?.medicalProducts?.banner?.[currentLang];
            const fallbackImg = isHomeProducts ? '/home-products-banner.jpg' : '/medical-products-banner.jpg';
            if (bannerByLang?.mediaType === 'video' && bannerByLang?.videoUrl) {
              return (
                <video className="absolute inset-0 w-full h-full object-cover" src={bannerByLang.videoUrl} autoPlay muted loop playsInline />
              );
            }
            const legacyImg = isHomeProducts 
              ? (settings?.pages?.categories?.homeProducts?.bannerImage)
              : (settings?.pages?.categories?.medicalProducts?.bannerImage);
            const bgUrl = bannerByLang?.imageUrl || legacyImg || fallbackImg;
            return (
              <div className="absolute inset-0 bg-center bg-cover" style={{ backgroundImage: `url('${bgUrl}')`, backgroundColor: '#1890ff' }} />
            );
          })()}
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          <div className="max-w-6xl mx-auto px-4 text-center relative z-10">
            <Title level={1} className="text-4xl font-bold text-white mb-4 drop-shadow-lg" style={{ color: '#ffffff' }}>
              {isHomeProducts 
                ? (getLocalizedText(settings?.pages?.categories?.homeProducts?.title) || pageTitle)
                : (getLocalizedText(settings?.pages?.categories?.medicalProducts?.title) || pageTitle)
              }
            </Title>
            <Paragraph className="text-xl text-white max-w-3xl mx-auto drop-shadow-lg">
              {isHomeProducts 
                ? (getLocalizedText(settings?.pages?.categories?.homeProducts?.subtitle) || pageDescription)
                : (getLocalizedText(settings?.pages?.categories?.medicalProducts?.subtitle) || pageDescription)
              }
            </Paragraph>
          </div>
        </section>

        {/* Header */}
        <section className="py-12 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-8">
              <Title level={2} className="text-3xl font-bold text-gray-900 mb-4">
                {t('categories.subcategories.title', 'Explore Categories')}
              </Title>
            </div>
          </div>
        </section>

        {/* Categories Grid */}
        <section className="py-8">
          <div className="max-w-6xl mx-auto px-4">
            {categoriesLoading ? (
              <div className="text-center py-12">
                <Spin size="large" />
                <p className="mt-4 text-gray-600">{t('categories.messages.loading')}</p>
              </div>
            ) : categoriesError ? (
              <div className="text-center py-12">
                <Empty 
                  description={t('categories.messages.failedToLoad')} 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </div>
            ) : categories.length === 0 ? (
              <div className="text-center py-12">
                <Empty 
                  description={t('categories.messages.noCategories')} 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </div>
            ) : (
              <Row gutter={[24, 24]}>
                {categories.map((category: any) => (
                  <Col xs={24} sm={12} lg={8} key={category._id}>
                    <Link to={`/products?category=${category.slug}`}>
                      <Card
                        hoverable
                        cover={
                          <div className="h-64 bg-white flex items-center justify-center p-4">
                            {category.image?.url ? (
                              <img 
                                src={category.image.url} 
                                alt={getLocalizedText(category.image.alt, category.name.en)}
                                className="w-full h-full object-contain rounded"
                              />
                            ) : (
                              <span className="text-gray-400">
                                {getLocalizedText(category.name, 'Category Image')}
                              </span>
                            )}
                          </div>
                        }
                        className="h-full"
                      >
                        <Card.Meta
                          title={
                            <div className="text-center">
                              <Title level={4} className="mb-2">
                                {getLocalizedText(category.name, 'Category')}
                              </Title>
                            </div>
                          }
                          description={
                            <div className="text-center">
                              <Text className="text-gray-600 block mb-3">
                                {getLocalizedText(category.description, 'Category description')}
                              </Text>
                            </div>
                          }
                        />
                      </Card>
                    </Link>
                  </Col>
                ))}
              </Row>
            )}
          </div>
        </section>
      </Content>
    </>
  )
}

export default CategoryPage